// ─── Town Center Map ──────────────────────────────────────────────────────────
// Safe area: Surggie Center (heal), Item Shop, friendly NPCs around the plaza.
// No encounter zones. South edge exits to Route 1. East edge exits to Beach Cave.
//
// TODO: src/engine/world-map.ts currently models the overworld as a runtime
// `WorldMap` class returning `RoomData` (tileMap/collisionMap/interactables).
// It does not export a static map-data interface matching the new
// {tiles, exits, npcs, buildings, trainers, encounterZones} shape used by the
// upcoming map registry. Until that interface lands, this module exports the
// map literal via `as const` so its structure is preserved without `any`.

// Tile codes (mirror src/engine/constants.ts TileType enum):
//   0 = Empty, 1 = Sand, 4 = Grass, 5 = BuildingFloor,
//   6 = BuildingExterior, 7 = Door, 8 = Wall, 10 = PalmTrunk, 12 = TikiBar.

const S = 1;  // Sand (plaza floor)
const G = 4;  // Grass (decorative edge)
const F = 5;  // BuildingFloor (roof tile, blocked)
const E = 6;  // BuildingExterior wall
const D = 7;  // Door (entry tile)
const P = 10; // Palm trunk (decorative obstacle)
const T = 12; // Tiki bar (acts as central fountain prop)

// 20 cols × 16 rows. Row 0 is the NORTH edge.
//
// Layout sketch:
//   rows 1–4   : Surggie Center building (cols 7–11) with door at (row 4, col 9)
//   rows 5–8   : Item Shop building (cols 15–18) with door at (row 8, col 16)
//   rows 7–8   : Central fountain (tiki bar) at cols 9–10
//   row  15    : South edge — exit to Route 1 at cols 9, 10
//   col  19    : East edge — exit to Beach Cave at row 9
const tiles: number[][] = [
  // row 0 — north grass border
  [G, G, G, G, G, G, G, G, G, G, G, G, G, G, G, G, G, G, G, G],
  // row 1 — Surggie Center top wall (cols 7..11)
  [G, S, S, S, S, S, S, E, E, E, E, E, S, S, S, S, S, S, S, G],
  // row 2 — Surggie Center interior
  [G, S, S, S, S, S, S, E, F, F, F, E, S, S, S, S, S, S, S, G],
  // row 3 — Surggie Center interior
  [G, S, S, S, S, S, S, E, F, F, F, E, S, S, S, S, S, S, S, G],
  // row 4 — Surggie Center bottom wall with door at col 9
  [G, S, S, S, S, S, S, E, E, D, E, E, S, S, S, S, S, S, S, G],
  // row 5 — plaza + Item Shop top wall (cols 15..18)
  [G, S, S, S, P, S, S, S, S, S, S, S, S, S, S, E, E, E, E, G],
  // row 6 — plaza + Item Shop interior
  [G, S, S, S, S, S, S, S, S, S, S, S, S, S, S, E, F, F, E, G],
  // row 7 — plaza fountain (left), Item Shop interior
  [G, S, S, S, S, S, S, S, S, T, T, S, S, S, S, E, F, F, E, G],
  // row 8 — plaza fountain (right), Item Shop bottom wall with door at col 16
  [G, S, S, S, S, S, S, S, S, T, T, S, S, S, S, E, D, E, E, G],
  // row 9 — plaza, EAST exit at col 19
  [G, S, S, S, S, S, S, S, S, S, S, S, S, S, S, S, S, S, S, S],
  // row 10 — plaza
  [G, S, S, S, S, S, S, S, S, S, S, S, S, S, S, S, S, S, P, G],
  // row 11 — plaza
  [G, S, S, S, P, S, S, S, S, S, S, S, S, S, S, S, S, S, S, G],
  // row 12 — plaza
  [G, S, S, S, S, S, S, S, S, S, S, S, S, S, S, S, S, S, S, G],
  // row 13 — plaza
  [G, S, S, S, S, S, S, S, S, S, S, S, S, S, S, S, P, S, S, G],
  // row 14 — plaza
  [G, S, S, S, S, S, S, S, S, S, S, S, S, S, S, S, S, S, S, G],
  // row 15 — south edge, exits to Route 1 at cols 9 and 10
  [G, G, G, G, G, G, G, G, G, S, S, G, G, G, G, G, G, G, G, G],
];

export const TOWN_CENTER_MAP = {
  id: "town_center",
  name: "Town Center",
  width: 20,
  height: 16,
  tiles,
  // Safe area — no wild encounters.
  encounterZones: [],
  // South edge → Route 1. East edge → Beach Cave (placeholder map id).
  exits: [
    { x: 9, y: 15, targetMapId: "route_1", targetX: 9, targetY: 1 },
    { x: 10, y: 15, targetMapId: "route_1", targetX: 10, targetY: 1 },
    { x: 19, y: 9, targetMapId: "beach_cave", targetX: 1, targetY: 9 },
  ],
  // No trainers in town.
  trainers: [],
  // 5 friendly NPCs scattered around the plaza.
  npcs: [
    {
      id: "npc_town_greeter",
      x: 5,
      y: 6,
      facing: "down",
      dialog: [
        "Welcome to Town Center!",
        "The Surggie Center up north will heal your team for free.",
        "Travelers from Route 1 always stop here first.",
      ],
    },
    {
      id: "npc_town_shopper",
      x: 13,
      y: 9,
      facing: "right",
      dialog: [
        "The shop sells catch balls and potions!",
        "Stock up before you head into the Beach Cave.",
      ],
    },
    {
      id: "npc_town_lost_kid",
      x: 8,
      y: 12,
      facing: "down",
      dialog: [
        "I lost my Salespup near the boardwalk...",
        "If you spot one, please bring it home!",
      ],
    },
    {
      id: "npc_town_old_trainer",
      x: 15,
      y: 11,
      facing: "left",
      dialog: [
        "Back in my day, we caught Surggies with cardboard boxes.",
        "Kids today have it easy with those fancy catch balls.",
        "Heh. Good luck out there, rookie.",
      ],
    },
    {
      id: "npc_town_fountain_poet",
      x: 11,
      y: 8,
      facing: "left",
      dialog: [
        "The fountain hums when a strong trainer walks by.",
        "Did you hear it just now? You must be the real deal.",
      ],
    },
    {
      id: "npc_town_beach_guide",
      x: 17,
      y: 9,
      facing: "left",
      dialog: [
        "East of here is the Beach Cave. Watch your step!",
        "Wild Surggies in there hit harder than you'd expect.",
      ],
    },
  ],
  buildings: [
    {
      id: "surggie_center",
      bounds: { x: 7, y: 1, w: 5, h: 4 },
      entryX: 9,
      entryY: 4,
      entryAction: "enter_surggie_center",
    },
    {
      id: "item_shop",
      bounds: { x: 15, y: 5, w: 4, h: 4 },
      entryX: 16,
      entryY: 8,
      entryAction: "enter_item_shop",
    },
  ],
} as const;

export type TownCenterMap = typeof TOWN_CENTER_MAP;
